import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PhoneIcon, CalendarDaysIcon, XIcon, SendIcon, CheckIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
const MONTH_NAMES_EN = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTH_NAMES_UA = ['Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень', 'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'];
const DAY_NAMES_EN = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAY_NAMES_UA = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Нд'];
function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}
function getFirstDayOfMonth(year: number, month: number) {
  const day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1;
}
function TelegramIcon({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" fill="currentColor" className={className} data-id="element-97">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" data-id="element-98" />
    </svg>;
}
function WhatsAppIcon({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" fill="currentColor" className={className} data-id="element-99">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" data-id="element-100" />
    </svg>;
}
/* ═══════════════════════════════════════════════
   CALENDAR WIDGET
   ═══════════════════════════════════════════════ */
function CalendarWidget() {
  const {
    t,
    language
  } = useLanguage();
  const now = new Date();
  const [currentMonth, setCurrentMonth] = useState(now.getMonth());
  const [currentYear, setCurrentYear] = useState(now.getFullYear());
  const [selectedDate, setSelectedDate] = useState<number | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const times = ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00'];
  const monthNames = language === 'ua' ? MONTH_NAMES_UA : MONTH_NAMES_EN;
  const dayNames = language === 'ua' ? DAY_NAMES_UA : DAY_NAMES_EN;
  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const firstDay = getFirstDayOfMonth(currentYear, currentMonth);
  const dates = Array.from({
    length: daysInMonth
  }, (_, i) => i + 1);
  const canGoPrev = !(currentYear === now.getFullYear() && currentMonth === now.getMonth());
  const goToPrevMonth = () => {
    if (!canGoPrev) return;
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(y => y - 1);
    } else setCurrentMonth(m => m - 1);
    setSelectedDate(null);
    setSelectedTime(null);
  };
  const goToNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(y => y + 1);
    } else setCurrentMonth(m => m + 1);
    setSelectedDate(null);
    setSelectedTime(null);
  };
  const isPastDate = (date: number) => new Date(currentYear, currentMonth, date) < new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const isToday = (date: number) => date === now.getDate() && currentMonth === now.getMonth() && currentYear === now.getFullYear();
  if (isConfirmed) {
    return <div className="flex flex-col items-center justify-center text-center py-6" data-id="element-101">
        <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3" style={{
        backgroundColor: 'rgba(16,185,129,0.1)'
      }} data-id="element-102">
          <CheckIcon className="w-6 h-6 text-emerald-500" data-id="element-103" />
        </div>
        <h3 className="text-base font-bold mb-1" style={{
        color: 'var(--text-primary)'
      }} data-id="element-104">
          {t('join.cal.success')}
        </h3>
        <p className="text-sm" style={{
        color: 'var(--text-secondary)'
      }} data-id="element-105">
          {selectedDate} {monthNames[currentMonth]} {currentYear},{' '}
          {selectedTime}
        </p>
      </div>;
  }
  return <div className="flex flex-col" data-id="element-106">
      <h3 className="text-base font-bold mb-3" style={{
      color: 'var(--text-primary)'
    }} data-id="element-107">
        {t('join.cal.title')}
      </h3>

      {/* Month nav */}
      <div className="flex items-center justify-between mb-3" data-id="element-108">
        <span className="font-bold text-sm" style={{
        color: 'var(--text-primary)'
      }} data-id="element-109">
          {monthNames[currentMonth]} {currentYear}
        </span>
        <div className="flex gap-1" data-id="element-110">
          <button onClick={goToPrevMonth} disabled={!canGoPrev} className="p-1.5 rounded-lg border transition-colors hover:bg-[var(--bg-tertiary)] disabled:opacity-30 disabled:cursor-not-allowed" style={{
          borderColor: 'var(--border)',
          color: 'var(--text-primary)'
        }} data-id="element-111">
            <ChevronLeftIcon className="w-3.5 h-3.5" data-id="element-112" />
          </button>
          <button onClick={goToNextMonth} className="p-1.5 rounded-lg border transition-colors hover:bg-[var(--bg-tertiary)]" style={{
          borderColor: 'var(--border)',
          color: 'var(--text-primary)'
        }} data-id="element-113">
            <ChevronRightIcon className="w-3.5 h-3.5" data-id="element-114" />
          </button>
        </div>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 gap-px mb-1" data-id="element-115">
        {dayNames.map(d => <div key={d} className="text-center text-[10px] font-medium py-1" style={{
        color: 'var(--text-muted)'
      }} data-id="element-116">
            {d}
          </div>)}
      </div>

      {/* Date grid — fixed height so it doesn't jump */}
      <div className="grid grid-cols-7 gap-px" style={{
      minHeight: '192px'
    }} data-id="element-117">
        {Array.from({
        length: firstDay
      }).map((_, i) => <div key={`e-${i}`} data-id="element-118" />)}
        {dates.map(date => {
        const sel = selectedDate === date;
        const past = isPastDate(date);
        const tod = isToday(date);
        return <button key={date} onClick={() => {
          if (!past) {
            setSelectedDate(date);
            setSelectedTime(null);
          }
        }} disabled={past} className={`aspect-square rounded-full flex items-center justify-center text-xs font-medium transition-colors ${past ? 'opacity-25 cursor-not-allowed' : sel ? '' : 'hover:bg-[var(--bg-tertiary)]'}`} style={{
          backgroundColor: sel ? 'var(--accent)' : 'transparent',
          color: sel ? '#FFF' : 'var(--text-primary)',
          ...(tod && !sel ? {
            boxShadow: 'inset 0 0 0 1.5px var(--accent)'
          } : {})
        }} data-id="element-119">
              {date}
            </button>;
      })}
      </div>

      {/* Time slots — no height animation, just fade */}
      {selectedDate && <div className="border-t mt-3 pt-3" style={{
      borderColor: 'var(--border)'
    }} data-id="element-120">
          <h4 className="text-xs font-bold mb-2" style={{
        color: 'var(--text-primary)'
      }} data-id="element-121">
            {t('join.cal.time')}
          </h4>
          <div className="grid grid-cols-3 gap-1.5 mb-3" data-id="element-122">
            {times.map(time => {
          const sel = selectedTime === time;
          return <button key={time} onClick={() => setSelectedTime(time)} className="py-1.5 rounded-lg border text-xs font-medium transition-colors" style={{
            borderColor: sel ? 'var(--accent)' : 'var(--border)',
            backgroundColor: sel ? 'var(--accent)' : 'var(--bg-tertiary)',
            color: sel ? '#FFF' : 'var(--text-primary)'
          }} data-id="element-123">
                  {time}
                </button>;
        })}
          </div>
          {selectedTime && <button onClick={() => setIsConfirmed(true)} className="w-full py-2.5 rounded-xl font-bold text-sm text-white transition-transform hover:scale-[1.02]" style={{
        backgroundColor: 'var(--accent)'
      }} data-id="element-124">
              {t('join.cal.confirm')}
            </button>}
        </div>}
    </div>;
}
/* ═══════════════════════════════════════════════
   CONTACT WIDGET
   ═══════════════════════════════════════════════ */
function ContactWidget() {
  const {
    language
  } = useLanguage();
  const [sent, setSent] = useState(false);
  const labels = {
    title: language === 'ua' ? "Зв'яжіться з нами" : 'Get in Touch',
    tg: 'Telegram',
    wa: 'WhatsApp',
    or: language === 'ua' ? 'або залиште контакт' : 'or leave your contact',
    name: language === 'ua' ? "Ваше ім'я" : 'Your name',
    contact: language === 'ua' ? 'Телефон або Email' : 'Phone or Email',
    send: language === 'ua' ? 'Надіслати' : 'Send',
    success: language === 'ua' ? "Дякуємо! Ми зв'яжемося з вами." : "Thank you! We'll get back to you."
  };
  if (sent) {
    return <div className="flex flex-col items-center justify-center text-center py-6" data-id="element-125">
        <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3" style={{
        backgroundColor: 'rgba(16,185,129,0.1)'
      }} data-id="element-126">
          <CheckIcon className="w-6 h-6 text-emerald-500" data-id="element-127" />
        </div>
        <p className="text-sm font-medium" style={{
        color: 'var(--text-primary)'
      }} data-id="element-128">
          {labels.success}
        </p>
      </div>;
  }
  return <div className="flex flex-col" data-id="element-129">
      <h3 className="text-base font-bold mb-4" style={{
      color: 'var(--text-primary)'
    }} data-id="element-130">
        {labels.title}
      </h3>

      {/* Messenger buttons */}
      <div className="flex gap-2 mb-4" data-id="element-131">
        <a href="https://t.me/itcluster" target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl font-medium text-sm border transition-colors hover:bg-[var(--bg-tertiary)]" style={{
        borderColor: 'var(--border)',
        color: 'var(--text-primary)'
      }} data-id="element-132">
          <TelegramIcon className="w-4 h-4" style={{
          color: 'var(--accent)'
        }} data-id="element-133" />
          {labels.tg}
        </a>
        <a href="https://wa.me/3612345678" target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl font-medium text-sm border transition-colors hover:bg-[var(--bg-tertiary)]" style={{
        borderColor: 'var(--border)',
        color: 'var(--text-primary)'
      }} data-id="element-134">
          <WhatsAppIcon className="w-4 h-4" style={{
          color: 'var(--accent)'
        }} data-id="element-135" />
          {labels.wa}
        </a>
      </div>

      {/* Divider */}
      <div className="flex items-center gap-3 mb-4" data-id="element-136">
        <div className="flex-1 h-px" style={{
        backgroundColor: 'var(--border)'
      }} data-id="element-137" />
        <span className="text-[11px] font-medium" style={{
        color: 'var(--text-muted)'
      }} data-id="element-138">
          {labels.or}
        </span>
        <div className="flex-1 h-px" style={{
        backgroundColor: 'var(--border)'
      }} data-id="element-139" />
      </div>

      {/* Contact form */}
      <form onSubmit={e => {
      e.preventDefault();
      setSent(true);
    }} className="space-y-2.5" data-id="element-140">
        <input type="text" required placeholder={labels.name} className="w-full rounded-xl border px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30" style={{
        borderColor: 'var(--border)',
        backgroundColor: 'var(--bg-tertiary)',
        color: 'var(--text-primary)'
      }} data-id="element-141" />
        <input type="text" required placeholder={labels.contact} className="w-full rounded-xl border px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30" style={{
        borderColor: 'var(--border)',
        backgroundColor: 'var(--bg-tertiary)',
        color: 'var(--text-primary)'
      }} data-id="element-142" />
        <button type="submit" className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl font-bold text-sm text-white transition-transform hover:scale-[1.02]" style={{
        backgroundColor: 'var(--accent)'
      }} data-id="element-143">
          {labels.send}
          <SendIcon className="w-3.5 h-3.5" data-id="element-144" />
        </button>
      </form>
    </div>;
}
/* ═══════════════════════════════════════════════
   MAIN FLOATING WIDGETS
   ═══════════════════════════════════════════════ */
export function FloatingWidgets() {
  const [openPanel, setOpenPanel] = useState<'calendar' | 'contact' | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const calBtnRef = useRef<HTMLButtonElement>(null);
  const contactBtnRef = useRef<HTMLButtonElement>(null);
  // Close on click outside (ignore clicks on trigger buttons)
  useEffect(() => {
    if (!openPanel) return;
    function handleClick(e: MouseEvent) {
      const target = e.target as Node;
      if (panelRef.current && !panelRef.current.contains(target) && calBtnRef.current && !calBtnRef.current.contains(target) && contactBtnRef.current && !contactBtnRef.current.contains(target)) {
        setOpenPanel(null);
      }
    }
    // Delay listener to avoid catching the opening click
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClick);
    }, 10);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [openPanel]);
  // Close on Escape
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpenPanel(null);
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);
  return <>
      {/* ── LEFT BOTTOM: Calendar trigger ── */}
      <button ref={calBtnRef} onClick={() => setOpenPanel(openPanel === 'calendar' ? null : 'calendar')} className={`fixed left-4 sm:left-6 bottom-6 z-[60] w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 ${openPanel === 'calendar' ? 'scale-90 opacity-70' : 'hover:scale-110'}`} style={{
      backgroundColor: 'var(--accent)',
      color: '#FFFFFF'
    }} aria-label="Open calendar" data-id="element-145">
        <CalendarDaysIcon className="w-5 h-5" data-id="element-146" />
      </button>

      {/* ── RIGHT BOTTOM: Contact trigger ── */}
      <button ref={contactBtnRef} onClick={() => setOpenPanel(openPanel === 'contact' ? null : 'contact')} className={`fixed right-4 sm:right-6 bottom-6 z-[60] w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 ${openPanel === 'contact' ? 'scale-90 opacity-70' : 'hover:scale-110'}`} style={{
      backgroundColor: 'var(--accent)',
      color: '#FFFFFF'
    }} aria-label="Open contact" data-id="element-147">
        <PhoneIcon className="w-5 h-5" data-id="element-148" />
      </button>

      {/* ── Overlay ── */}
      <AnimatePresence data-id="element-149">
        {openPanel && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15
      }} className="fixed inset-0 z-[55] bg-black/15" onClick={() => setOpenPanel(null)} data-id="element-150" />}
      </AnimatePresence>

      {/* ── Panel ── */}
      <AnimatePresence mode="wait" data-id="element-151">
        {openPanel && <motion.div ref={panelRef} key={openPanel} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 20
      }} transition={{
        duration: 0.2,
        ease: [0.25, 0.46, 0.45, 0.94]
      }} className={`fixed z-[60] rounded-2xl border shadow-2xl p-4 
              ${openPanel === 'calendar' ? 'left-4 sm:left-6' : 'right-4 sm:right-6'}
              bottom-20 sm:bottom-20
              w-[calc(100vw-2rem)] sm:w-[320px]
              max-h-[calc(100vh-7rem)]
              overflow-y-auto overscroll-contain
            `} style={{
        borderColor: 'var(--border)',
        backgroundColor: 'var(--bg-primary)'
      }} data-id="element-152">
            {/* Close button */}
            <button onClick={() => setOpenPanel(null)} className="absolute top-3 right-3 p-1 rounded-lg transition-colors hover:bg-[var(--bg-tertiary)] z-10" style={{
          color: 'var(--text-muted)'
        }} aria-label="Close" data-id="element-153">
              <XIcon className="w-4 h-4" data-id="element-154" />
            </button>

            {openPanel === 'calendar' ? <CalendarWidget data-id="element-155" /> : <ContactWidget data-id="element-156" />}
          </motion.div>}
      </AnimatePresence>
    </>;
}